import React, {useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {FaEdit, FaEye, FaShapes} from "react-icons/fa";
import {Badge, Button, Flex, Text} from "@chakra-ui/react";
import {AddIcon} from "@chakra-ui/icons";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import ListPageCardHeader from "components/Card/ListPageCardHeader";
import Loading from "components/Loading/Loading";
import {useCustomToast} from "components/CustomToast/CustomToast";
import {ROUTES} from "constants/routes-path";
import {boatService} from "services/boatService";
import {getEmptyPageResponse, toTablePageableData} from "utils";
import SearchTable2 from "components/Tables/SearchTable2";

function BoatList() {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState(undefined);
    const [searchParams, setSearchParams] = useState({});
    const [response, setResponse] = useState(undefined);
    const navigate = useNavigate();
    const showToast = useCustomToast();

    const columns = useMemo(() => [
        {
            Header: t("boat.list.table.header.boat-number"),
            accessor: "boatNumber",
        },
        {
            Header: t("boat.list.table.header.created-at"),
            accessor: "createdAt",
            custom: ({ value }) => {
                return new Date(value).toLocaleString()
            }
        },
        {
            Header: t("boat.list.table.header.vehicles-plates"),
            accessor: "vehiclePlates",
            custom: ({ value }) => {
                return value.map((plate, index) => (
                    <Badge variant="solid" colorScheme='purple' key={`plate-${index}`} mr="5px">{plate}</Badge>
                ))
            }
        },
        {
            Header: t("boat.list.table.header.status"),
            accessor: "status",
            custom: ({ value }) => value === 'RASCUNHO' ? 'RASCUNHO' : 'FINALIZADO',
        },
        {
            Header: t("boat.list.table.header.operations"),
            disableSortBy: true,
            actions: [
                {
                    label: t('system.actions.edit'),
                    onClick: (row) => navigate(ROUTES.boatsEditPath.replace(":id", row.original.id)),
                    icon: <FaEdit />,
                    visible: (row) => row.status === 'RASCUNHO',
                },
                {
                    label: t('boat.actions.add-drawing'),
                    onClick: (row) => navigate(ROUTES.boatsDrawingPath.replace(":id", row.original.id)),
                    icon: <FaShapes />,
                    visible: (row) => row.status === 'FINALIZADO_SEM_DESENHO',
                },
                {
                    label: t('system.actions.details'),
                    onClick: (row) => navigate(ROUTES.boatsDetailsPath.replace(":id", row.original.id)),
                    icon: <FaEye />,
                    visible: (row) => row.status !== 'RASCUNHO',
                }
            ],
        },
    ], []);

    const newBoat = () => {
        navigate(ROUTES.boatsAddPath)
    }

    const fetchData = async (params = {}) => {
        if (response) setResponse(undefined);

        try {
            const page = Math.min(params?.page ?? 1, response?.total_pages ?? Infinity) || 1;
            params = { ...searchParams, ...params, page: page };
            setSearchParams(params);
            const data = await boatService.list(params);
            setResponse(data);
        } catch (err) {
            setResponse(getEmptyPageResponse(searchParams));
            showToast({ title: t(err), status: "error" });
        }
    };

    useEffect(() => {
        fetchData()
    }, [])

    const onSearch = (term) => {
        setSearchTerm(term);
        const payload = {
            page: defaultPageable.page,
            pageSize: searchParams?.pageSize || defaultPageable.pageSize,
            termo: term
        }
        fetchData(payload);
    }

    return (
        <Flex gap="5" direction="column" pt={{ base: "150px", lg: "90px" }}>
            <Flex w="100%" px="30px" justify="space-between" align="center" direction={{ sm: "column", md: "row" }}>
                <Button
                    variant="list-primary"
                    leftIcon={<AddIcon fontSize="sm" />}
                    alignSelf={{ sm: "flex-start", lg: undefined }}
                    onClick={newBoat}
                >
                    {t('boat.actions.new-boat')}
                </Button>
            </Flex>

            <Card overflowX={{ sm: "scroll", lg: "hidden" }}>
                <ListPageCardHeader title={t("boat.title")} subtitle={t("boat.description")} />

                <CardBody>
                    {response ? (
                        <SearchTable2
                            columnsData={columns}
                            tableData={response.content}
                            pageableData={toTablePageableData(response, { defaultField: "id", ...searchParams })}
                            searchTerm={searchTerm}
                            setSearchTerm={onSearch}
                            onChange={fetchData}
                        />
                    ) : (
                        <Loading maxH={{ sm: "100px", md: "350px" }} />
                    )}
                </CardBody>
            </Card>
        </Flex>
    );
}

export default BoatList;